import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { DealData } from "@/pages/deals/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { cn } from "@/utils/utils";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import { CompanyData } from "@/pages/companies/types";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import UserAvatar from "@/components/controls/UserAvatar";
import SingleSelect from "@/components/controls/SingleSelect";
import { ReservedUserIds } from "@/entities/users/types";
import { Add } from "@dreamhub/designsystem/icons";
import { LeadData } from "@/pages/leads/types";
import { ContractData } from "@/pages/contracts/types";
import IconContainer from "@/components/ui/icon-container";
import { get, isEmpty, map } from "lodash";
import { useActiveFieldKeys } from "@/pages/settings/hooks";

interface Props {
  entity: DealData | CompanyData | LeadData | ContractData | undefined;
  entityType:
    | EntityType.DEAL
    | EntityType.COMPANY
    | EntityType.LEAD
    | EntityType.CONTRACT;
  onUpdateField: (field: string, newValue: string | number) => void;
  className?: string;
  isQuickView?: boolean;
}

const Ownership: React.FC<Props> = ({
  entity,
  entityType,
  onUpdateField,
  className,
  isQuickView = false
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  const { activeFieldKeys, isLoaded: fieldsLoaded } =
    useActiveFieldKeys(entityType);
  const fields = (() => {
    const allFields = (() => {
      switch (entityType) {
        case EntityType.DEAL:
          return ["managedBy"];
        case EntityType.LEAD:
          return ["aeOwner", "sdrOwner"];
        case EntityType.CONTRACT:
          return ["aeOwner", "csmOwner"];
        default:
          return ["aeOwner", "sdrOwner", "csmOwner"];
      }
    })();
    if (!fieldsLoaded) return allFields;
    return allFields.filter((field) => activeFieldKeys.has(field));
  })();
  const ownerIds = map(fields, (field) => get(entity, field));
  const { users } = useUsers(ownerIds);
  const {
    options: userOptions,
    isLoading: isLoadingUsers,
    setSearchTerm: setUserSearchTerm
  } = useUserSelect();
  const [activeField, setActiveField] = useState<string | null>(null);

  const handleActiveFieldChange = (nextField: string | null) => {
    setActiveField(nextField);
    if (nextField === null) {
      setUserSearchTerm("");
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-5 border-[0.5px] border-border-border1 w-full rounded-xl",
        isQuickView ? "bg-surface-elevation2 p-4" : "bg-surface-elevation1 p-6",
        className
      )}
      data-test={`${entityType}-ownership`}
    >
      <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"}>
        {t("companies.ownership.title")}
      </Text>
      <div className="flex w-full" data-test={`${entityType}-ownership-list`}>
        {map(users, (userData, index) => {
          const fieldName = fields[index];
          const isUnknownUser = userData.id === ReservedUserIds.UNKNOWN_USER_ID;
          return (
            <div
              className="flex-1 flex gap-3 items-center"
              key={index}
              data-test={`${entityType}-ownership-${fieldName}`}
            >
              {isUnknownUser ? (
                <div
                  className={cn(
                    "group size-10 p-1 rounded-2xl flex items-center justify-center cursor-pointer border border-dashed border-border-border3 transition-all duration-200 ease-in-out hover:bg-surface-hover-strong",
                    !isEmpty(activeField) && "bg-surface-hover-strong"
                  )}
                  onClick={() => handleActiveFieldChange(fieldName)}
                >
                  <IconContainer
                    color="tertiary"
                    size="5"
                    className={cn(
                      "transition-all duration-200 ease-in-out group-hover:!text-primary",
                      !isEmpty(activeField) && "!text-primary"
                    )}
                  >
                    <Add />
                  </IconContainer>
                </div>
              ) : (
                <UserAvatar
                  userData={userData}
                  size="XXL"
                  fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary"
                />
              )}
              <div className="flex flex-col mr-2">
                <Text variant="bodySecondary" className="ml-2">
                  {t(`companies.ownership.${fieldName}`)}
                </Text>
                <SingleSelect
                  key={fieldName}
                  fieldName={fieldName}
                  entity={EntityType.USER}
                  variant="inline"
                  options={userOptions}
                  selectedValue={get(entity, fieldName)}
                  onSelection={(newValue) => {
                    onUpdateField(fieldName, newValue);
                    handleActiveFieldChange(null);
                  }}
                  // Widths belong to the redesigned ownership row; ungated they
                  // changed the trigger and popover on Company, Deal and Lead
                  // for everyone.
                  className={cn(
                    "min-h-fit text-start",
                    isNewFieldsUI && "min-w-28"
                  )}
                  popoverClassName={isNewFieldsUI ? "min-w-64" : undefined}
                  isOpen={activeField === fieldName}
                  onOpenChange={(isOpen) => {
                    if (isOpen) return handleActiveFieldChange(fieldName);
                    handleActiveFieldChange(null);
                  }}
                  mainContainerClassName="!shadow-none"
                  showSearch
                  onSearch={setUserSearchTerm}
                  isLoading={isLoadingUsers}
                  clearable
                  onClear={() => onUpdateField(fieldName, "")}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Ownership;
